<template>
  <v-container fluid>
    <!-- Android Management API -->
    <v-card outlined class="mb-4" v-if="amapi">
      <v-card-title class="subtitle-1">
        <v-icon small left>fa-shield-alt</v-icon> Zarządzanie tabletami (Android Enterprise)
        <v-spacer></v-spacer>
        <v-btn small text color="primary" :loading="syncing" @click="sync" v-if="amapi.enterpriseName"><v-icon small left>fa-sync</v-icon> Synchronizuj urządzenia</v-btn>
      </v-card-title>
      <v-card-text>
        <div v-if="!amapi.enabled" class="grey--text">Integracja wyłączona w konfiguracji serwera (AndroidManagement:Enabled). Tablety rejestruje się ręcznie kodem.</div>
        <div v-else-if="!amapi.configured" class="orange--text">Brak konfiguracji po stronie serwera: projekt Google Cloud i konto serwisowe (AndroidManagement:ProjectId, ServiceAccountJsonPath).</div>
        <div v-else-if="amapi.enterpriseName">
          Przedsiębiorstwo: <b>{{ amapi.displayName || amapi.enterpriseName }}</b> <span class="grey--text caption">({{ amapi.enterpriseName }})</span>
          · aplikacja {{ amapi.appPackageName }} · adres API dla tabletów {{ amapi.terminalBaseUrl }}
          <span v-if="amapi.lastSyncAt" class="grey--text"> · ostatnia synchronizacja {{ formatDateTime(amapi.lastSyncAt) }}</span>
          <div class="red--text" v-if="amapi.lastSyncError">{{ amapi.lastSyncError }}</div>
        </div>
        <div v-else>
          <p>Przedsiębiorstwo Android Enterprise nie jest jeszcze zarejestrowane. Rejestrację wykonuje administrator podmiotu kontem Google (bez opłat).</p>
          <v-btn color="primary" small :loading="signupLoading" @click="startSignup"><v-icon small left>fa-google</v-icon> Rozpocznij rejestrację u Google</v-btn>
          <span class="mx-2 grey--text">albo</span>
          <v-text-field v-model="attachName" placeholder="enterprises/LC..." dense outlined hide-details class="d-inline-block" style="width: 260px"></v-text-field>
          <v-btn small text color="primary" :disabled="!attachName" @click="attach">Podepnij istniejące</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-row align="center">
      <v-col cols="12" md="6"><v-btn color="info" @click="addNew"><v-icon left small>fa-plus</v-icon> Dodaj terminal</v-btn></v-col>
      <v-col cols="12" md="6" class="text-right"><v-btn icon @click="load" title="Odśwież"><v-icon small>fa-sync</v-icon></v-btn></v-col>
    </v-row>

    <v-data-table :items="terminals" :headers="headers" :loading="loading" hide-default-footer disable-pagination no-data-text="Brak terminali">
      <template v-slot:item.code="{ item }">
        <div><b>{{ item.code }}</b> <span class="grey--text">{{ item.name }}</span></div>
        <div class="grey--text caption">{{ item.location }} · {{ labels.context[item.context] }}</div>
      </template>
      <template v-slot:item.state="{ item }">
        <v-chip x-small :color="item.active ? (item.enrolled ? 'green' : 'orange') : 'grey'" dark>
          {{ !item.active ? 'wyłączony' : item.enrolled ? 'zarejestrowany' : 'niezarejestrowany' }}
        </v-chip>
        <v-chip x-small outlined class="ml-1" v-if="item.enrollmentPending" color="blue">kod ważny do {{ formatTime(item.enrollmentExpiresAt) }}</v-chip>
        <div class="grey--text caption" v-if="item.lastSeenAt">ostatni kontakt {{ formatDateTime(item.lastSeenAt) }}<span v-if="item.appVersion"> · app {{ item.appVersion }}</span></div>
      </template>
      <template v-slot:item.amapi="{ item }">
        <div v-if="item.amapiDeviceName">
          <v-chip x-small :color="item.amapiState === 'ACTIVE' ? 'green' : 'orange'" dark>{{ item.amapiState }}</v-chip>
          <span class="grey--text caption ml-1">{{ item.amapiModel }} {{ item.amapiSerial }}</span>
          <div class="grey--text caption" v-if="item.amapiLastReportAt">raport {{ formatDateTime(item.amapiLastReportAt) }}<span v-if="item.amapiPolicyCompliant === false" class="red--text"> · niezgodny z polityką</span></div>
        </div>
        <span v-else class="grey--text caption">-</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="edit(item)" title="Edytuj">fa-edit</v-icon>
        <v-icon small class="mr-2" @click="enrollmentCode(item)" title="Kod rejestracyjny (ręcznie)" v-if="item.active">fa-key</v-icon>
        <v-icon small class="mr-2" @click="enrollmentQr(item)" title="Kod QR (Android Enterprise)" v-if="item.active && amapi && amapi.enterpriseName">fa-qrcode</v-icon>
        <v-icon small class="mr-2" @click="revoke(item)" title="Unieważnij poświadczenie" v-if="item.enrolled">fa-unlink</v-icon>
        <v-icon small class="mr-2" @click="command(item, 'LOCK')" title="Zablokuj ekran" v-if="item.amapiDeviceName">fa-lock</v-icon>
        <v-icon small class="mr-2" @click="command(item, 'REBOOT')" title="Uruchom ponownie" v-if="item.amapiDeviceName">fa-power-off</v-icon>
        <v-icon small class="mr-2" @click="wipe(item)" title="Wyczyść urządzenie (reset)" color="red" v-if="item.amapiDeviceName">fa-eraser</v-icon>
      </template>
    </v-data-table>

    <!-- aktywne sesje -->
    <v-card outlined class="mt-4" v-if="sessions.length">
      <v-card-title class="subtitle-1">Sesje w toku</v-card-title>
      <v-simple-table dense>
        <thead><tr><th>Tablet</th><th>Pacjent</th><th>Dokument</th><th>Status</th><th>Ważna do</th><th></th></tr></thead>
        <tbody>
          <tr v-for="s in sessions" :key="s.sessionId">
            <td>{{ s.terminalCode }}</td><td>{{ s.clientName }}</td><td>{{ s.templateTitle }}</td>
            <td><v-chip x-small :color="labels.sessionColor[s.status]" dark>{{ labels.sessionStatus[s.status] }}</v-chip></td>
            <td>{{ formatTime(s.expiresAt) }}</td>
            <td><v-btn x-small text color="error" @click="cancelSession(s)">Anuluj</v-btn></td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- dialog edycji -->
    <v-dialog v-model="showEdit" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark class="primary" dense><v-toolbar-title>{{ form.id ? 'Terminal' : 'Nowy terminal' }}</v-toolbar-title></v-toolbar>
        <v-card-text class="pt-4">
          <v-text-field v-model="form.code" label="Kod (np. RECEPCJA-TAB-01)" outlined dense :disabled="!!form.id"></v-text-field>
          <v-text-field v-model="form.name" label="Nazwa" outlined dense></v-text-field>
          <v-text-field v-model="form.location" label="Lokalizacja" outlined dense></v-text-field>
          <v-select v-model="form.context" :items="contexts" label="Kontekst zbierania oświadczeń" outlined dense></v-select>
          <v-checkbox v-model="form.active" label="Aktywny" v-if="form.id" hide-details></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showEdit = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- dialog kodu / QR -->
    <v-dialog v-model="showEnroll" max-width="560px">
      <v-card v-if="enroll">
        <v-toolbar dark class="primary" dense>
          <v-btn icon @click="showEnroll = false" dark><v-icon>fa-times</v-icon></v-btn>
          <v-toolbar-title>Rejestracja tabletu {{ enroll.terminalCode }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4 text-center">
          <div v-if="enroll.qrPngBase64">
            <img :src="'data:image/png;base64,' + enroll.qrPngBase64" style="width: 280px; height: 280px" alt="QR" />
            <p class="mt-2">Na tablecie po resecie fabrycznym, na ekranie powitalnym, dotknij ekran 6 razy i zeskanuj kod.
              Tablet zainstaluje aplikację i zarejestruje się sam.</p>
          </div>
          <div class="display-1 font-weight-bold my-3" style="letter-spacing: 6px">{{ enroll.code || enroll.enrollmentCode }}</div>
          <div class="grey--text">Kod rejestracyjny do ręcznego wpisania na ekranie konfiguracji tabletu. Ważny do {{ formatDateTime(enroll.expiresAt) }}, jednorazowy.</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPin" max-width="420px" persistent>
      <v-card>
        <v-card-title class="subtitle-1">PIN administratora tabletu</v-card-title>
        <v-card-text>
          <p class="grey--text">Zostanie przekazany na tablet w konfiguracji zarządzanej i zapisany tam jako hash. Puste = bez PIN-u.</p>
          <v-text-field v-model="adminPin" label="PIN (4-8 cyfr)" outlined dense type="password"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPin = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="qrLoading" @click="generateQr">Generuj QR</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Panel "Terminale" (PLAN.md sekcja 5.2): tablety, kody rejestracyjne, Android Management API.
import { podpisyService, podpisyLabels, abpError } from "@/services/podpisy.service";
import { mapActions } from "vuex";
import swal from "sweetalert";
import dayjs from "dayjs";

export default {
  name: "SigningTerminals",
  data() {
    return {
      loading: false, saving: false, syncing: false, signupLoading: false, qrLoading: false,
      terminals: [], sessions: [], amapi: null,
      showEdit: false, form: {},
      showEnroll: false, enroll: null,
      showPin: false, pinTarget: null, adminPin: "",
      attachName: "",
      labels: podpisyLabels,
      contexts: Object.keys(podpisyLabels.context).map(k => ({ value: k, text: podpisyLabels.context[k] })),
      headers: [
        { text: "Terminal", value: "code", sortable: false },
        { text: "Stan", value: "state", sortable: false },
        { text: "Android Enterprise", value: "amapi", sortable: false },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],
      timer: null,
    };
  },
  methods: {
    ...mapActions("alert", ["error", "success"]),
    ...mapActions("title", ["updateTitle"]),
    formatDateTime(v) { return v ? dayjs(v).format("DD-MM-YYYY HH:mm") : ""; },
    formatTime(v) { return v ? dayjs(v).format("HH:mm") : ""; },
    load() {
      this.loading = true;
      Promise.all([podpisyService.getTerminals(), podpisyService.getEnterpriseStatus(), podpisyService.getActiveSessions()])
        .then(([t, e, s]) => { this.terminals = t.data.result; this.amapi = e.data.result; this.sessions = s.data.result; })
        .catch(e => this.error(abpError(e, "Nie można pobrać terminali")))
        .finally(() => { this.loading = false; });
    },
    addNew() { this.form = { code: "", name: "", location: "", context: "RECEPTION", active: true }; this.showEdit = true; },
    edit(t) { this.form = { id: t.id, code: t.code, name: t.name, location: t.location, context: t.context, active: t.active }; this.showEdit = true; },
    save() {
      this.saving = true;
      const req = this.form.id ? podpisyService.updateTerminal(this.form) : podpisyService.createTerminal(this.form);
      req.then(() => { this.showEdit = false; this.success("Zapisano"); this.load(); })
        .catch(e => this.error(abpError(e, "Nie można zapisać terminala")))
        .finally(() => { this.saving = false; });
    },
    enrollmentCode(t) {
      podpisyService.generateEnrollmentCode(t.id).then(resp => { this.enroll = resp.data.result; this.showEnroll = true; this.load(); })
        .catch(e => this.error(abpError(e, "Nie można wygenerować kodu")));
    },
    enrollmentQr(t) { this.pinTarget = t; this.adminPin = ""; this.showPin = true; },
    generateQr() {
      this.qrLoading = true;
      podpisyService.createEnrollmentQr(this.pinTarget.id, this.adminPin || null).then(resp => {
        this.showPin = false; this.enroll = resp.data.result; this.showEnroll = true; this.load();
      }).catch(e => this.error(abpError(e, "Nie można wygenerować kodu QR")))
        .finally(() => { this.qrLoading = false; });
    },
    revoke(t) {
      swal({ title: "Unieważnić poświadczenie?", text: t.code + " przestanie działać do ponownej rejestracji.", icon: "warning", buttons: ["Nie", "Unieważnij"], dangerMode: true })
        .then(ok => { if (ok) podpisyService.revokeCredential(t.id).then(() => this.load()).catch(e => this.error(abpError(e, "Błąd"))); });
    },
    command(t, cmd) {
      podpisyService.issueCommand(t.id, cmd).then(() => this.success("Komenda " + cmd + " wysłana")).catch(e => this.error(abpError(e, "Błąd komendy")));
    },
    wipe(t) {
      swal({ title: "Wyczyścić urządzenie?", text: "Tablet " + t.code + " zostanie przywrócony do ustawień fabrycznych przez Google, a poświadczenie unieważnione.", icon: "warning", buttons: ["Nie", "Wyczyść"], dangerMode: true })
        .then(ok => { if (ok) podpisyService.wipeDevice(t.id).then(() => { this.success("Wysłano polecenie wyczyszczenia"); this.load(); }).catch(e => this.error(abpError(e, "Błąd"))); });
    },
    sync() {
      this.syncing = true;
      podpisyService.syncDevices().then(resp => { this.terminals = resp.data.result; this.success("Zsynchronizowano"); this.load(); })
        .catch(e => this.error(abpError(e, "Błąd synchronizacji")))
        .finally(() => { this.syncing = false; });
    },
    startSignup() {
      this.signupLoading = true;
      const callback = window.location.origin + this.$router.resolve({ name: "SigningTerminals" }).href;
      podpisyService.createSignupUrl(callback).then(resp => { window.location.href = resp.data.result; })
        .catch(e => { this.error(abpError(e, "Nie można rozpocząć rejestracji")); this.signupLoading = false; });
    },
    completeSignup(token) {
      podpisyService.completeSignup(token, null).then(() => { this.success("Przedsiębiorstwo zarejestrowane"); this.load(); })
        .catch(e => this.error(abpError(e, "Nie można dokończyć rejestracji u Google")));
    },
    attach() {
      podpisyService.attachEnterprise(this.attachName).then(() => { this.success("Podpięto"); this.load(); })
        .catch(e => this.error(abpError(e, "Nie można podpiąć przedsiębiorstwa")));
    },
    cancelSession(s) {
      swal({ title: "Anulować sesję?", text: s.clientName + " · " + s.templateTitle, icon: "warning", buttons: ["Nie", "Anuluj sesję"], dangerMode: true })
        .then(ok => { if (ok) podpisyService.cancelSession(s.sessionId, "USER", "Anulowano z panelu terminali").then(() => this.load()).catch(e => this.error(abpError(e, "Błąd"))); });
    },
  },
  created() {
    this.updateTitle("Terminale (tablety do podpisu)");
  },
  mounted() {
    this.load();
    const token = this.$route.query.enterpriseToken;
    if (token) {
      this.$router.replace({ name: "SigningTerminals" });
      this.completeSignup(token);
    }
    this.timer = setInterval(() => { if (!this.showEdit && !this.showEnroll && !this.showPin) this.load(); }, 15000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>
